import { usePermission } from "../hooks/usePermission";
import React, { useEffect, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import {
  Plus, X, Pencil, Trash2,
  Phone, MessageSquare, Mail, MessageCircle, MoreHorizontal,
  Calendar, FileText, ChevronRight, Search, Filter, User as UserIcon, XCircle,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const TYPE_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  WHATSAPP: { label: "WhatsApp", color: "bg-green-100 text-green-700",   icon: <MessageCircle size={13}/> },
  CALL:     { label: "Call",     color: "bg-blue-100 text-blue-700",    icon: <Phone size={13}/> },
  CHAT:     { label: "Chat",     color: "bg-purple-100 text-purple-700", icon: <MessageSquare size={13}/> },
  EMAIL:    { label: "Email",    color: "bg-yellow-100 text-yellow-700", icon: <Mail size={13}/> },
  OTHER:    { label: "Other",    color: "bg-gray-100 text-gray-700",     icon: <MoreHorizontal size={13}/> },
};
const TYPES = Object.keys(TYPE_CONFIG);
const blank = { type: "CALL", customType: "", customerName: "", customerPhone: "", summary: "", request: "", actionTaken: "" };

const DetailDrawer: React.FC<{
  entry: any;
  onClose: () => void;
  onEdit: (e: any) => void;
  onDelete: (id: string) => void;
  canEdit: boolean;
}> = ({ entry, onClose, onEdit, onDelete, canEdit }) => {
  const cfg = TYPE_CONFIG[entry.type] || TYPE_CONFIG.OTHER;
  const displayType = entry.type === "OTHER" && entry.customType ? entry.customType : cfg.label;
  const date = new Date(entry.date || entry.createdAt).toLocaleDateString("en-GB", {
    day: "2-digit", month: "long", year: "numeric",
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-lg bg-white h-full flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 bg-white">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full font-medium ${cfg.color}`}>
                {cfg.icon} {displayType}
              </span>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Calendar size={11}/> {date}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">
              {entry.customerName || "Unknown Customer"}
            </h2>
            {entry.customerPhone && (
              <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1">
                <Phone size={12}/> {entry.customerPhone}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {canEdit && (
              <>
                <button
                  onClick={() => { onClose(); onEdit(entry); }}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 border border-gray-200 hover:border-blue-300 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Pencil size={12}/> Edit
                </button>
                <button
                  onClick={() => { onClose(); onDelete(entry.id); }}
                  className="flex items-center gap-1 text-xs text-red-400 hover:text-red-600 border border-red-100 hover:border-red-300 px-3 py-1.5 rounded-lg transition-colors"
                >
                  <Trash2 size={12}/> Delete
                </button>
              </>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1.5">
              <X size={18}/>
            </button>
          </div>
        </div>

        {/* Employee badge */}
        {entry.employee?.name && (
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                {entry.employee.name[0].toUpperCase()}
              </div>
              <div>
                <p className="text-xs text-gray-400">Handled by</p>
                <p className="text-sm font-semibold text-gray-800">{entry.employee.name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {entry.summary && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-blue-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                <FileText size={11}/> Summary
              </p>
              <p className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">{entry.summary}</p>
            </div>
          )}

          {entry.request && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Customer Request</p>
              <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
                <p className="text-sm text-orange-900 leading-relaxed whitespace-pre-wrap">{entry.request}</p>
              </div>
            </div>
          )}

          {entry.actionTaken && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Action Taken</p>
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                <p className="text-sm text-green-900 leading-relaxed whitespace-pre-wrap">{entry.actionTaken}</p>
              </div>
            </div>
          )}

          {/* Meta grid */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">Type</p>
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>
                  {cfg.icon} {displayType}
                </span>
              </div>
              <div className="bg-gray-50 rounded-xl p-3">
                <p className="text-xs text-gray-400 mb-1">Date</p>
                <p className="text-sm font-medium text-gray-700">{date}</p>
              </div>
              {entry.customerName && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Customer</p>
                  <p className="text-sm font-medium text-gray-700">{entry.customerName}</p>
                </div>
              )}
              {entry.customerPhone && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-400 mb-1">Phone</p>
                  <p className="text-sm font-medium text-gray-700">{entry.customerPhone}</p>
                </div>
              )}
              {entry.employee?.name && (
                <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs text-gray-400 mb-1">Employee</p>
                  <p className="text-sm font-medium text-gray-700">{entry.employee.name}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Communications: React.FC = () => {
  const { isAdmin, isManager } = useAuth();
  const canEditComm   = usePermission("communications", "edit");
  const canDeleteComm = usePermission("communications", "delete");
  const [entries, setEntries]       = useState<any[]>([]);
  const [users, setUsers]           = useState<any[]>([]);
  const [showForm, setShowForm]     = useState(false);
  const [editEntry, setEditEntry]   = useState<any>(null);
  const [viewEntry, setViewEntry]   = useState<any>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [form, setForm]             = useState(blank);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch]           = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo]     = useState("");

  const load = () => {
    const params: any = {};
    if (typeFilter) params.type = typeFilter;
    api.get("/communications", { params }).then(r => setEntries(r.data));
  };
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
  }, [typeFilter]);

  const resetFilters = () => { setSearch(""); setFilterEmployee(""); setFilterDateFrom(""); setFilterDateTo(""); };
  const hasFilters = search || filterEmployee || filterDateFrom || filterDateTo;

  const filtered = entries.filter(e => {
    if (filterEmployee && e.employee?.id !== filterEmployee) return false;
    if (filterDateFrom && new Date(e.date || e.createdAt) < new Date(filterDateFrom)) return false;
    if (filterDateTo && new Date(e.date || e.createdAt) > new Date(filterDateTo + "T23:59:59")) return false;
    if (search) {
      const q = search.toLowerCase();
      const match = (e.customerName || "").toLowerCase().includes(q) ||
        (e.customerPhone || "").toLowerCase().includes(q) ||
        (e.summary || "").toLowerCase().includes(q) ||
        (e.request || "").toLowerCase().includes(q) ||
        (e.actionTaken || "").toLowerCase().includes(q);
      if (!match) return false;
    }
    return true;
  });

  const openCreate = () => { setEditEntry(null); setForm(blank); setShowForm(true); };
  const openEdit   = (e: any) => {
    setEditEntry(e);
    setForm({
      type: e.type, customType: e.customType || "",
      customerName: e.customerName || "", customerPhone: e.customerPhone || "",
      summary: e.summary || "", request: e.request || "", actionTaken: e.actionTaken || "",
    });
    setShowForm(true);
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    try {
      if (editEntry) { await api.put(`/communications/${editEntry.id}`, form); toast.success("Updated!"); }
      else           { await api.post("/communications", form); toast.success("Log added!"); }
      setShowForm(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this log?")) return;
    try {
      await api.delete(`/communications/${id}`);
      toast.success("Deleted");
      setViewEntry(null);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Communications</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(f => !f)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showFilters ? "bg-blue-600 text-white" : "border border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
            <Filter size={14}/> Filters
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Plus size={16}/> Add Log
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Filter size={14}/> Filter Communications
            </div>
            <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600"><X size={16}/></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search customer, phone, summary, request..."
                  className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"><X size={14}/></button>}
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Employee</label>
              <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white">
                <option value="">All Employees</option>
                {users.map((u: any) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
            </div>
            <div className="flex items-end gap-2">
              {hasFilters && (
                <button onClick={resetFilters}
                  className="flex items-center gap-1 border border-gray-300 text-gray-600 px-3 py-2 rounded-lg text-sm hover:bg-gray-50">
                  <XCircle size={14}/> Reset
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {["", ...TYPES].map(t => (
          <button key={t} onClick={() => setTypeFilter(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              typeFilter === t
                ? "bg-blue-600 text-white"
                : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
            }`}>
            {t ? TYPE_CONFIG[t]?.label : "All"}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {["Type", "Customer", "Phone", "Summary", "Request", "Action Taken", "Employee", "Date", ...((canEditComm || canDeleteComm) ? ["Actions"] : [])].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium text-gray-600 text-xs">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(e => {
              const cfg = TYPE_CONFIG[e.type] || TYPE_CONFIG.OTHER;
              const displayType = e.type === "OTHER" && e.customType ? e.customType : cfg.label;
              return (
                <tr
                  key={e.id}
                  onClick={() => setViewEntry(e)}
                  className="hover:bg-blue-50/40 cursor-pointer group transition-colors"
                >
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${cfg.color}`}>
                      {cfg.icon} {displayType}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    <span className="flex items-center gap-1">
                      {e.customerName || "-"}
                      <ChevronRight size={13} className="text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity"/>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{e.customerPhone || "-"}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-[140px]">
                    <span className="block truncate">{e.summary || "-"}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-[140px]">
                    <span className="block truncate">{e.request || "-"}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 max-w-[140px]">
                    <span className="block truncate">{e.actionTaken || "-"}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{e.employee?.name || "-"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                    {new Date(e.date || e.createdAt).toLocaleDateString()}
                  </td>
                  {(canEditComm || canDeleteComm) && (
                    <td className="px-4 py-3" onClick={ev => ev.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {canEditComm && (
                          <button onClick={() => openEdit(e)} title="Edit"
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                            <Pencil size={14} />
                          </button>
                        )}
                        {canDeleteComm && (
                          <button onClick={() => remove(e.id)} title="Delete"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <MessageSquare size={32} className="mx-auto mb-2 opacity-30"/>
            <p>No communication logs found</p>
          </div>
        )}
      </div>

      {entries.length > 0 && (
        <p className="text-xs text-gray-400">Click on any row to view full details</p>
      )}

      {viewEntry && (
        <DetailDrawer
          entry={viewEntry}
          onClose={() => setViewEntry(null)}
          onEdit={openEdit}
          onDelete={remove}
          canEdit={canEditComm || canDeleteComm}
        />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-md my-4">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold">{editEntry ? "Edit Log" : "Add Communication Log"}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value, customType: "" })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                  {TYPES.map(t => <option key={t} value={t}>{TYPE_CONFIG[t].label}</option>)}
                </select>
              </div>
              {form.type === "OTHER" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Custom Type</label>
                  <input value={form.customType} onChange={e => setForm({ ...form, customType: e.target.value })}
                    placeholder="e.g. Telegram, LinkedIn..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input value={form.customerName} onChange={e => setForm({ ...form, customerName: e.target.value })}
                    placeholder="Customer Name"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input value={form.customerPhone} onChange={e => setForm({ ...form, customerPhone: e.target.value })}
                    placeholder="Phone Number"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summary</label>
                <textarea value={form.summary} onChange={e => setForm({ ...form, summary: e.target.value })}
                  placeholder="Brief summary of the communication" rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Request</label>
                <textarea value={form.request} onChange={e => setForm({ ...form, request: e.target.value })}
                  placeholder="What did the customer request?" rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Taken</label>
                <textarea value={form.actionTaken} onChange={e => setForm({ ...form, actionTaken: e.target.value })}
                  placeholder="What action was taken?" rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"/>
              </div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg hover:bg-gray-50 text-sm">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">
                  {editEntry ? "Save Changes" : "Add Log"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Communications;
